import { createSlice, createAsyncThunk, createSelector, } from '@reduxjs/toolkit';
import { DateUtils, CONS, } from '@pomofocus/core';
import { getApiClient } from '../../utils/apiClient';
import { ReportUtils } from '../../utils';
// Async thunks
const getActivitySummary = createAsyncThunk('report/getActivitySummary', async (_, { rejectWithValue }) => {
    try {
        const todayDigit = DateUtils.convertDateToDateDigit(new Date());
        const response = await getApiClient()({
            method: 'get',
            url: '/api/summary-report',
            data: {
                todayDigit,
            },
        });
        const { daysAccessed, minutesFocused, dayStreak } = response.data;
        const timeFocused = Math.round(minutesFocused / 60);
        return {
            daysAccessed,
            timeFocused,
            dayStreak,
        };
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
// const incrementDailyWorkMinutes = createAsyncThunk(
//   'report/incrementDailyWorkMinutes',
//   async (
//     {
//       id,
//       dateStr,
//       dateObj,
//       dateDigit,
//       nowMinutes,
//       addingMinutes,
//       projectName,
//       task,
//     }: {
//       id: string;
//       dateStr: string;
//       dateObj: Date;
//       dateDigit: number;
//       nowMinutes: number;
//       addingMinutes: number;
//       projectName: string;
//       task: string;
//     },
//     { rejectWithValue },
//   ) => {
//     try {
//       await getApiClient()({
//         method: 'post',
//         url: '/api/daily-work-minutes',
//         data: {
//           id,
//           dateStr,
//           dateObj,
//           nowMinutes,
//           addingMinutes,
//           dateDigit,
//           projectName,
//           task,
//           timezoneOffset: dateObj.getTimezoneOffset(),
//         },
//       });
//       return Date.now();
//     } catch (error) {
//       return rejectWithValue(error);
//     }
//   },
// );
const fetchRankingHoursWeek = createAsyncThunk('report/fetchRankingHoursWeek', async (rankPage, { rejectWithValue }) => {
    try {
        const dateDigitList = ReportUtils.generateDateList(new Date(), 7);
        const response = await getApiClient()({
            method: 'get',
            url: '/api/ranking-hours',
            data: {
                dateDigitList,
                rankPage,
            },
        });
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
// the logged-in user's rank and surrounding rows (rank -2 .. +2)
const fetchMyRankingWeek = createAsyncThunk('report/fetchMyRankingWeek', async (_, { rejectWithValue }) => {
    try {
        const dateDigitList = ReportUtils.generateDateList(new Date(), 7);
        const response = await getApiClient()({
            method: 'get',
            url: '/api/ranking-hours-me',
            data: {
                dateDigitList,
            },
        });
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
const downloadReportData = createAsyncThunk('report/downloadReportData', async ({ dateFromDigit, dateToDigit, includeTask, focusTime, delimiter, onDownload, onError, }, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            method: 'get',
            url: '/api/report-data-csv',
            data: {
                dateFromDigit,
                dateToDigit,
                includeTask,
                focusTime,
                delimiter,
            },
        });
        const { csvContent } = response.data;
        onDownload?.(csvContent);
        return csvContent;
    }
    catch (error) {
        onError?.(error);
        return rejectWithValue(error);
    }
});
const fetchDailyReportItems = createAsyncThunk('report/fetchDailyReportItems', async (data, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            method: 'get',
            url: '/api/daily-report-items',
            data: { pageNum: data.pageNum },
        });
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
const deleteDailyReportItem = createAsyncThunk('report/deleteDailyReportItem', async (data, { rejectWithValue, dispatch }) => {
    try {
        const todayDigit = DateUtils.convertDateToDateDigit(new Date());
        await getApiClient()({
            method: 'post',
            url: '/api/delete-daily-report-item',
            data: { id: data.id, todayDigit },
        });
        dispatch(fetchDailyReportItems({ pageNum: data.pageNum }));
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
const upsertDailyReportItem = createAsyncThunk('report/upsertDailyReportItem', async (data, { rejectWithValue, dispatch }) => {
    const todayDigit = DateUtils.convertDateToDateDigit(new Date());
    try {
        await getApiClient()({
            method: 'post',
            url: '/api/upsert-daily-report-item',
            data: {
                dailyReportItem: data.reportItem,
                todayDigit,
            },
        });
        dispatch(fetchDailyReportItems({ pageNum: data.pageNum }));
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
const upsertDailyReportItemManually = createAsyncThunk('report/upsertDailyReportItemManually', async ({ reportItem, todayDigit, }, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            method: 'post',
            url: '/api/upsert-daily-report-item-manually',
            data: {
                reportItem,
                todayDigit,
            },
        });
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
const changeProjectName = createAsyncThunk('report/changeProjectName', async ({ before, after, dateDigitStart, dateDigitEnd, reportType, backCnt, }, { rejectWithValue, dispatch }) => {
    try {
        await getApiClient()({
            method: 'post',
            url: '/api/change-project-name',
            data: { before, after, dateDigitStart, dateDigitEnd },
        });
        // Dispatch appropriate fetch based on report type
        if (reportType === CONS.REPORT_TYPE_YEAR) {
            dispatch(fetchMonthlyWorkHoursThisYear(backCnt));
        }
        else if (reportType === CONS.REPORT_TYPE_WEEK) {
            dispatch(fetchWeeklyWorkHoursThisMonth(backCnt));
        }
        else if (reportType === CONS.REPORT_TYPE_DAY) {
            dispatch(fetchDailyWorkHoursThisWeek(backCnt));
        }
        return { success: true };
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
// const fetchDailyWorkMinutes = createAsyncThunk(
//   'report/fetchDailyWorkMinutes',
//   async (_, { dispatch }) => {
//     const sevenDaysStrings = ReportUtils.generateDaysString(7, 0);
//     const datasets = ReportUtils.generateDataset(
//       sevenDaysStrings,
//       [],
//     );
//     return { labels: sevenDaysStrings, datasets };
//   },
// );
const fetchDailyWorkHoursThisWeek = createAsyncThunk('report/fetchDailyWorkHoursThisWeek', async (backCnt, { dispatch, getState, rejectWithValue }) => {
    try {
        const weekStartDay = ReportUtils.getWeekStartDay(getState().config.config.weekStart);
        const endOfTheWeek = ReportUtils.getEndOfTheWeek(new Date(), weekStartDay);
        const { dateStr } = DateUtils.convertDateToString(endOfTheWeek);
        const response = await getApiClient()({
            method: 'get',
            url: '/api/daily-work-minutes',
            data: {
                todayStr: dateStr,
                daysBefore: 7,
                backCnt,
            },
        });
        const { reportsResult, dateDigitStart, dateDigitEnd } = response.data;
        const sevenDaysStrings = ReportUtils.generateDaysString(7, backCnt, endOfTheWeek);
        const dailyWorkHours = ReportUtils.changeLabel(reportsResult);
        const datasets = ReportUtils.generateDataset(sevenDaysStrings, dailyWorkHours);
        // Also dispatch getActivitySummary
        dispatch(getActivitySummary());
        dispatch(reportActions.updateDailyWorkMinutes({
            labels: sevenDaysStrings,
            datasets,
            dateDigitStart,
            dateDigitEnd,
        }));
        return {
            labels: sevenDaysStrings,
            datasets,
            dateDigitStart,
            dateDigitEnd,
        };
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
const fetchWeeklyWorkHoursThisMonth = createAsyncThunk('report/fetchWeeklyWorkHoursThisMonth', async (backCnt, { dispatch, getState, rejectWithValue }) => {
    try {
        const weekStartDay = ReportUtils.getWeekStartDay(getState().config.config.weekStart);
        const endOfTheWeek = ReportUtils.getEndOfTheWeek(new Date(), weekStartDay);
        const { dateStr } = DateUtils.convertDateToString(endOfTheWeek);
        const response = await getApiClient()({
            method: 'get',
            url: '/api/daily-work-minutes',
            data: {
                todayStr: dateStr,
                daysBefore: 7 * 4, // 4 weeks
                backCnt,
            },
        });
        const { reportsResult, dateDigitStart, dateDigitEnd } = response.data;
        const fiveWeeksStrings = ReportUtils.generateWeeksString(endOfTheWeek, 4, backCnt);
        const weeklyWorkHours = ReportUtils.changeLabelFromDateToWeek(reportsResult, weekStartDay);
        const datasets = ReportUtils.generateDataset(fiveWeeksStrings, weeklyWorkHours);
        // Also dispatch getActivitySummary
        dispatch(getActivitySummary());
        dispatch(reportActions.updateDailyWorkMinutes({
            labels: fiveWeeksStrings,
            datasets,
            dateDigitStart,
            dateDigitEnd,
        }));
        return {
            labels: fiveWeeksStrings,
            datasets,
            dateDigitStart,
            dateDigitEnd,
        };
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
const fetchMonthlyWorkHoursThisYear = createAsyncThunk('report/fetchMonthlyWorkHoursThisYear', async (backCnt, { dispatch, rejectWithValue }) => {
    try {
        const thisYear = new Date().getFullYear();
        const lastDayOfTheYear = new Date(thisYear, 11, 31);
        const { dateStr } = DateUtils.convertDateToString(lastDayOfTheYear);
        const response = await getApiClient()({
            method: 'get',
            url: '/api/daily-work-minutes',
            data: {
                todayStr: dateStr,
                daysBefore: 365,
                backCnt,
            },
        });
        const { reportsResult, dateDigitStart, dateDigitEnd } = response.data;
        const twelveMonthStrings = ReportUtils.generateMonthsString(thisYear, backCnt);
        const monthlyWorkHours = ReportUtils.changeFromDateToMongh(reportsResult);
        const datasets = ReportUtils.generateDataset(twelveMonthStrings, monthlyWorkHours);
        // Also dispatch getActivitySummary
        dispatch(getActivitySummary());
        dispatch(reportActions.updateDailyWorkMinutes({
            labels: twelveMonthStrings,
            datasets,
            dateDigitStart,
            dateDigitEnd,
        }));
        return {
            labels: twelveMonthStrings,
            datasets,
            dateDigitStart,
            dateDigitEnd,
        };
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
// Initial state
const initialState = {
    dailyWorkMinutes: [],
    rankPage: 0,
    ranking: [],
    rankingHasMore: true,
    myRank: null,
    myRankingContext: [],
    loadingRanking: false,
    labels: [],
    datasets: [],
    reportType: CONS.REPORT_TYPE_DAY,
    reportBackCnt: 0,
    focusHourToday: 0,
    downloading: false,
    loading: false,
    lastUpdateTS: 0,
    dailyReportItems: [],
    loadingDailyReportItems: false,
    dateDigitStart: 0,
    dateDigitEnd: 0,
    error: null,
    // activity summary
    daysAccessed: undefined,
    timeFocused: undefined,
    dayStreak: undefined,
};
// Report slice
const reportSlice = createSlice({
    name: 'report',
    initialState,
    reducers: {
        // Update daily work minutes (chart data)
        updateDailyWorkMinutes: (state, action) => {
            state.labels = action.payload.labels;
            state.datasets = action.payload.datasets;
            if (action.payload.dateDigitStart !== undefined) {
                state.dateDigitStart = action.payload.dateDigitStart;
            }
            if (action.payload.dateDigitEnd !== undefined) {
                state.dateDigitEnd = action.payload.dateDigitEnd;
            }
            state.loading = false;
        },
        // Change report type
        changeReportControl: (state, action) => {
            state.reportType = action.payload.reportType;
            state.reportBackCnt = action.payload.reportBackCnt;
        },
        initializeReportState: (state) => {
            state.labels = [];
            state.datasets = [];
            state.dateDigitStart = 0;
            state.dateDigitEnd = 0;
            state.reportType = CONS.REPORT_TYPE_DAY;
            state.reportBackCnt = 0;
        },
        // Change rank page
        changeRankPage: (state, action) => {
            state.rankPage = action.payload.rankPage;
        },
        // Start loading daily work hours
        startDailyWorkHourLoad: (state) => {
            state.loading = true;
        },
        // Clear error
        clearError: (state) => {
            state.error = null;
        },
        // Set focus hour today
        setFocusHourToday: (state, action) => {
            state.focusHourToday = action.payload.focusHourToday;
        },
        // Set daily work minutes directly
        setDailyWorkMinutes: (state, action) => {
            state.dailyWorkMinutes = action.payload.dailyWorkMinutes;
        },
        // Reset report state
        resetReportState: () => initialState,
    },
    extraReducers: (builder) => {
        // Get activity summary
        builder
            .addCase(getActivitySummary.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(getActivitySummary.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.daysAccessed = action.payload.daysAccessed;
            state.timeFocused = action.payload.timeFocused;
            state.dayStreak = action.payload.dayStreak;
        })
            .addCase(getActivitySummary.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
        // Increment daily work minutes
        // builder
        //   .addCase(incrementDailyWorkMinutes.pending, state => {
        //     state.error = null;
        //   })
        //   .addCase(
        //     incrementDailyWorkMinutes.fulfilled,
        //     (state, action) => {
        //       state.lastUpdateTS = action.payload;
        //       state.error = null;
        //     },
        //   )
        //   .addCase(
        //     incrementDailyWorkMinutes.rejected,
        //     (state, action) => {
        //       state.error = action.payload as string;
        //     },
        //   );
        // Fetch ranking hours week
        builder
            .addCase(fetchRankingHoursWeek.pending, (state) => {
            state.loadingRanking = true;
            state.error = null;
        })
            .addCase(fetchRankingHoursWeek.fulfilled, (state, action) => {
            state.loadingRanking = false;
            state.ranking = action.payload.ranking || [];
            state.rankingHasMore = action.payload.hasMore !== false;
            state.error = null;
        })
            .addCase(fetchRankingHoursWeek.rejected, (state, action) => {
            state.loadingRanking = false;
            state.error = action.payload;
        });
        // Fetch the current user's rank and surrounding rows
        builder
            .addCase(fetchMyRankingWeek.fulfilled, (state, action) => {
            state.myRank = action.payload.myRank ?? null;
            state.myRankingContext = action.payload.myContext || [];
        })
            .addCase(fetchMyRankingWeek.rejected, (state) => {
            state.myRank = null;
            state.myRankingContext = [];
        });
        // Download report data
        builder
            .addCase(downloadReportData.pending, (state) => {
            state.downloading = true;
            state.error = null;
        })
            .addCase(downloadReportData.fulfilled, (state) => {
            state.downloading = false;
            state.error = null;
        })
            .addCase(downloadReportData.rejected, (state, action) => {
            state.downloading = false;
            state.error = action.payload;
        });
        // Fetch daily report items
        builder
            .addCase(fetchDailyReportItems.pending, (state) => {
            state.loadingDailyReportItems = true;
            state.error = null;
        })
            .addCase(fetchDailyReportItems.fulfilled, (state, action) => {
            state.loadingDailyReportItems = false;
            state.dailyReportItems =
                action.payload.dailyReportItems || [];
            state.error = null;
        })
            .addCase(fetchDailyReportItems.rejected, (state, action) => {
            state.loadingDailyReportItems = false;
            state.error = action.payload;
        });
        // just to update label of the chart
        builder.addCase(fetchDailyWorkHoursThisWeek.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchDailyWorkHoursThisWeek.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
        });
        builder.addCase(fetchWeeklyWorkHoursThisMonth.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchWeeklyWorkHoursThisMonth.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
        });
        builder.addCase(fetchMonthlyWorkHoursThisYear.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchMonthlyWorkHoursThisYear.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
        });
        // Fetch daily work minutes
        // builder
        //   .addCase(fetchDailyWorkMinutes.pending, state => {
        //     state.loading = true;
        //     state.error = null;
        //   })
        //   .addCase(fetchDailyWorkMinutes.fulfilled, (state, action) => {
        //     state.loading = false;
        //     state.labels = action.payload.labels;
        //     state.datasets = action.payload.datasets;
        //     state.error = null;
        //   })
        //   .addCase(fetchDailyWorkMinutes.rejected, (state, action) => {
        //     state.loading = false;
        //     state.error = action.payload as string;
        //   });
        // Fetch daily work hours this week
    },
});
// Selectors
const selectReportState = (state) => state.report;
const selectDailyWorkMinutes = (state) => state.report.dailyWorkMinutes;
const selectRankPage = (state) => state.report.rankPage;
const selectRanking = (state) => state.report.ranking;
const selectRankingHasMore = (state) => state.report.rankingHasMore;
const selectMyRank = (state) => state.report.myRank;
const selectMyRankingContext = (state) => state.report.myRankingContext;
const selectLoadingRanking = (state) => state.report.loadingRanking;
const selectLabels = (state) => state.report.labels;
const selectDatasets = (state) => state.report.datasets;
const selectReportControl = (state) => ({
    reportType: state.report.reportType,
    reportBackCnt: state.report.reportBackCnt,
});
const selectFocusHourToday = (state) => state.report.focusHourToday;
const selectDownloading = (state) => state.report.downloading;
const selectReportLoading = (state) => state.report.loading;
const selectLastUpdateTS = (state) => state.report.lastUpdateTS;
const selectDailyReportItems = (state) => state.report.dailyReportItems;
const selectLoadingDailyReportItems = (state) => state.report.loadingDailyReportItems;
const selectDateDigitStart = (state) => state.report.dateDigitStart;
const selectDateDigitEnd = (state) => state.report.dateDigitEnd;
const selectReportError = (state) => state.report.error;
// Individual selectors for activity summary fields
const selectDaysAccessed = (state) => state.report.daysAccessed;
const selectTimeFocused = (state) => state.report.timeFocused;
const selectDayStreak = (state) => state.report.dayStreak;
// Memoized selector that combines activity summary fields
const selectActivitySummary = createSelector([selectDaysAccessed, selectTimeFocused, selectDayStreak], (daysAccessed, timeFocused, dayStreak) => ({
    daysAccessed,
    timeFocused,
    dayStreak,
}));
// Grouped exports for better organization
export const reportActions = {
    ...reportSlice.actions,
    // Async actions
    getActivitySummary,
    // incrementDailyWorkMinutes,
    fetchRankingHoursWeek,
    fetchMyRankingWeek,
    downloadReportData,
    fetchDailyReportItems,
    deleteDailyReportItem,
    upsertDailyReportItem,
    upsertDailyReportItemManually,
    changeProjectName,
    fetchDailyWorkHoursThisWeek,
    fetchWeeklyWorkHoursThisMonth,
    fetchMonthlyWorkHoursThisYear,
};
export const reportSelectors = {
    selectReportState,
    selectDailyWorkMinutes,
    selectRankPage,
    selectRanking,
    selectRankingHasMore,
    selectMyRank,
    selectMyRankingContext,
    selectLoadingRanking,
    selectLabels,
    selectDatasets,
    selectReportControl,
    selectFocusHourToday,
    selectDownloading,
    selectReportLoading,
    selectLastUpdateTS,
    selectDailyReportItems,
    selectLoadingDailyReportItems,
    selectDateDigitStart,
    selectDateDigitEnd,
    selectReportError,
    selectActivitySummary,
};
// Export reducer
export default reportSlice.reducer;
